/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/accounts/AccountHierarchySection.tsx
 * PURPOSE: Parent/subsidiary hierarchy strip in AccountDrawer (hierarchy bridge — ADR bridge to public.accounts)
 * PUBLIC_API: AccountHierarchySection
 */

import { Building2, ChevronRight, Link2 } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { SubsidiaryLinkModal } from '@domain/sales';
import { useSalesAccountHierarchy } from '../../hooks/useSalesAccountHierarchy';
import { CollapsibleSection } from '../contacts/ContactDrawerBodyChrome';
import { EmptyState } from '../shared/EmptyState';

export type AccountHierarchySectionProps = {
  salesAccountId: string;
  readOnly?: boolean;
  onNavigateAway?: () => void;
};

export function AccountHierarchySection({
  salesAccountId,
  readOnly = false,
  onNavigateAway,
}: AccountHierarchySectionProps): ReactElement {
  const {
    immediateParent,
    ancestorChain,
    subsidiaryCount,
    directSubsidiaries,
    loading,
    refetch,
  } = useSalesAccountHierarchy(salesAccountId);
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const hierarchyUrl = `/accounts/${salesAccountId}?tab=subsidiaries`;
  const hasParent = Boolean(immediateParent);
  const hasSubsidiaries = subsidiaryCount > 0 || directSubsidiaries.length > 0;
  const isTopLevel = !hasParent && !hasSubsidiaries;

  return (
    <CollapsibleSection title="Corporate hierarchy">
      <div className="space-y-3" data-testid="account-section-hierarchy">
        {loading ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">Loading hierarchy…</p>
        ) : isTopLevel ? (
          <EmptyState
            title="Top-level account"
            description="No parent company linked. Link subsidiaries to build a corporate structure."
            icon={<Building2 className="h-10 w-10 text-gray-300 dark:text-gray-600" aria-hidden />}
          />
        ) : null}

        {hasParent ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-600 dark:bg-gray-900/40">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Parent company
            </p>
            <Link
              to={`/accounts/${immediateParent!.id}`}
              className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
              data-testid="account-hierarchy-parent-link"
              onClick={() => onNavigateAway?.()}
            >
              {immediateParent!.name}
            </Link>
            {ancestorChain.length > 1 ? (
              <p className="mt-2 flex flex-wrap items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                {ancestorChain.slice(1).map((a, i) => (
                  <span key={a.id} className="inline-flex items-center gap-1">
                    {i > 0 ? <ChevronRight className="h-3 w-3" aria-hidden /> : null}
                    <Link
                      to={`/accounts/${a.id}`}
                      className="hover:text-primary-600 dark:hover:text-primary-400"
                      onClick={() => onNavigateAway?.()}
                    >
                      {a.name}
                    </Link>
                  </span>
                ))}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-gray-800 dark:text-gray-200" data-testid="account-hierarchy-subsidiary-count">
            <span className="font-semibold tabular-nums">{subsidiaryCount}</span>{' '}
            {subsidiaryCount === 1 ? 'subsidiary' : 'subsidiaries'}
          </p>
          <Link
            to={hierarchyUrl}
            className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
            data-testid="account-hierarchy-manage-link"
            onClick={() => onNavigateAway?.()}
          >
            Manage hierarchy
          </Link>
        </div>

        {directSubsidiaries.length > 0 ? (
          <ul className="space-y-1.5">
            {directSubsidiaries.slice(0, 5).map((sub) => (
              <li key={sub.id}>
                <Link
                  to={`/accounts/${sub.id}`}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                  data-testid={`account-hierarchy-subsidiary-${sub.id}`}
                  onClick={() => onNavigateAway?.()}
                >
                  <Building2 className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                  <span className="truncate">{sub.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}

        {!readOnly ? (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            data-testid="account-hierarchy-link-subsidiary"
            onClick={() => setLinkModalOpen(true)}
          >
            <Link2 className="h-3.5 w-3.5" aria-hidden />
            Link subsidiary
          </button>
        ) : null}

        {linkModalOpen ? (
          <SubsidiaryLinkModal
            parentAccountId={salesAccountId}
            onClose={() => {
              setLinkModalOpen(false);
              void refetch();
            }}
          />
        ) : null}
      </div>
    </CollapsibleSection>
  );
}
